import { useEffect, useRef, type ReactNode } from 'react';
import { Banner } from './banner';
import { Button } from './button';
import { ErrorState } from './state-views';

export interface InfiniteListProps<T> {
  items: T[];
  getKey: (item: T) => string;
  renderItem: (item: T, index: number) => ReactNode;
  /** Mirrors `useInfiniteQuery`'s fields so the query result can be spread in. */
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Shown when loading finished with no items. */
  emptyState: ReactNode;
  /** One skeleton row, repeated while loading / fetching the next page. */
  skeletonRow: ReactNode;
  skeletonCount?: number;
  className?: string;
}

/**
 * Cursor-paged list with an IntersectionObserver sentinel and a skeleton footer - no page numbers
 * (KT §5.6). Rendering of rows, filters and the query itself stay with the caller.
 */
export function InfiniteList<T>({
  items,
  getKey,
  renderItem,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  isLoading,
  isError,
  onRetry,
  emptyState,
  skeletonRow,
  skeletonCount = 3,
  className,
}: InfiniteListProps<T>) {
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasNextPage || isFetchingNextPage || isError) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) fetchNextPage();
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  const skeletons = Array.from({ length: skeletonCount }, (_, i) => <div key={`s${i}`}>{skeletonRow}</div>);

  if (isLoading) return <div className={className}>{skeletons}</div>;

  if (isError && items.length === 0) {
    return <ErrorState title="Couldn’t load this list" onRetry={onRetry} />;
  }

  if (items.length === 0) return <>{emptyState}</>;

  return (
    <div className={className}>
      {items.map((item, i) => (
        <div key={getKey(item)}>{renderItem(item, i)}</div>
      ))}
      {isFetchingNextPage ? skeletons : null}
      {isError ? (
        <Banner
          tone="error"
          title="Couldn’t load more"
          action={
            <Button variant="secondary" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        />
      ) : null}
      <div ref={sentinel} aria-hidden />
    </div>
  );
}
